export type ToolStatus = 'active' | 'coming-soon' | 'archived' | 'retired' | 'needs-review';

export type ToolSupportModel =
  | 'free'
  | 'donation'
  | 'paid'
  | 'subscription'
  | 'external';

export interface ToolItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  platform?: string;
  image?: string;
  destinationUrl: string;
  pricingLabel?: string;
  supportModel?: ToolSupportModel;
  featured?: boolean;
  status: ToolStatus;
  sortOrder: number;
  source: 'projects' | 'extra-links' | 'product' | 'external' | 'fourthwall' | 'gumroad';
  notes?: string;
}

export const supportDevelopmentUrl = 'https://buymeacoffee.com/austindavenport';
export const discordInviteUrl = 'https://discord.com/invite/vuKtEXJ';
export const youtubeChannelUrl = 'https://www.youtube.com/@AustinDavenport';
const fourthwallBaseUrl = 'https://austindavenport-shop.fourthwall.com/products';

export const toolCatalog: ToolItem[] = [
  {
    id: 'clipboardit',
    title: 'ClipboardIt',
    description: 'A smarter clipboard for Premiere Pro.',
    category: 'Premiere Pro',
    platform: 'Premiere Pro',
    image: 'https://imgproxy.fourthwall.com/NSD2usD9hYTysLDRn1nsl2le1Wj4LLGV3f-bqk1H1X0/w:720/sm:1/enc/OTk0NmI0NjQ1Yzli/YTczNN7v8sTooFr_/69APhnk512swjQmE/LU9wZ_gIvt3bh8II/9lan9TDd1fgslTKJ/_rVZKHQCjnTmPrYM/UTZavpQa9yN10avg/fBm3bDJjQOw1jAVI/S4jsp4uU7ius6dKQ/D20X9SLihVh0HP6e/8hBCkYiyyD_uUslv/E8aUFXGNSvdoX8no/KeRqwkLy-Jf2qmDF/OG5FrQ.webp',
    destinationUrl: 'https://clipboard.it.com/',
    pricingLabel: 'Paid',
    supportModel: 'paid',
    featured: true,
    status: 'active',
    sortOrder: 10,
    source: 'projects',
    notes: 'Current project page spells this as "Clipboard It"; links/store normalize to the product spelling "ClipboardIt".',
  },
  {
    id: 'youtool-io',
    title: 'YouTool.io',
    description: 'Free tools that make YouTube easier.',
    category: 'YouTube Tools',
    platform: 'Web',
    image: 'https://64.media.tumblr.com/377e88f933f314338ba9608900174dc9/7c0180d0d1212074-5a/s1280x1920/a048ccb1bec91251584811a90d7551c9f7b4ceb7.pnj',
    destinationUrl: 'https://www.youtool.io',
    pricingLabel: 'Free',
    supportModel: 'free',
    featured: true,
    status: 'active',
    sortOrder: 20,
    source: 'projects',
  },
  {
    id: 'link-in-bio-page-maker',
    title: 'Link-In-Bio Page Maker',
    description: 'Build a first-party link page for your creator brand.',
    category: 'Creator Utilities',
    platform: 'Web',
    image: '/images/warehouse/products/link-in-bio-page-maker-cover.png',
    destinationUrl: 'https://www.youtool.io/tools/link-in-bio-builder',
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 30,
    source: 'extra-links',
    notes: 'Migrated from /extra-links copy action to a direct store destination.',
  },
  {
    id: 'notion-templates',
    title: 'Notion Templates',
    description: 'Planner templates for content creators.',
    category: 'Templates',
    platform: 'Notion',
    image: '/images/warehouse/products/notion-templates.png',
    destinationUrl: 'https://www.notion.so/@austindavenport',
    pricingLabel: 'Templates',
    supportModel: 'external',
    status: 'active',
    sortOrder: 40,
    source: 'projects',
  },
  {
    id: 'smooth-it',
    title: 'Smooth It',
    description: 'Create and apply custom easing curves to keyframed properties.',
    category: 'Premiere Pro',
    platform: 'Adobe Exchange',
    image: 'https://64.media.tumblr.com/ac6b24eb89f0c1a5968cdeff134b1d87/de37173ea8e82b4a-76/s1280x1920/be943a97ff80dbace88c7692a05f7f3747901641.pnj',
    destinationUrl: 'https://exchange.adobe.com/apps/cc/fc1418c4/smooth-it',
    pricingLabel: 'Free',
    supportModel: 'external',
    status: 'active',
    sortOrder: 50,
    source: 'projects',
  },
  {
    id: 'youtube-blueprint',
    title: 'The YouTube Blueprint',
    description: 'A guide to growing a YouTube channel.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/youtube-blueprint.png',
    destinationUrl: `${fourthwallBaseUrl}/the-youtube-blueprint-ebook`,
    pricingLabel: 'Paid',
    supportModel: 'paid',
    status: 'active',
    sortOrder: 60,
    source: 'fourthwall',
  },
  {
    id: 'thumbnail-pack',
    title: "Austin's Thumbnail Pack",
    description: 'Editable thumbnail assets for YouTube creators.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/thumbnail-pack.webp',
    destinationUrl: `${fourthwallBaseUrl}/austins-thumbnail-pack`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 65,
    source: 'fourthwall',
  },
  {
    id: 'youtube-blueprint-desk-mat',
    title: 'The YouTube Blueprint Desk Mat',
    description: 'A desk mat built around the YouTube Blueprint design.',
    category: 'Merch',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/fourthwall-youtube-blueprint-desk-mat.jpg',
    destinationUrl: `${fourthwallBaseUrl}/the-youtube-blueprint-desk-mat`,
    pricingLabel: 'Paid',
    supportModel: 'paid',
    status: 'active',
    sortOrder: 64,
    source: 'fourthwall',
  },
  {
    id: 'folder-structure',
    title: 'Premiere Pro Template Save / Folder',
    description: 'A clean project folder structure for video work.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/folder-structure.webp',
    destinationUrl: `${fourthwallBaseUrl}/templatefolder`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'archived',
    sortOrder: 66,
    source: 'fourthwall',
  },
  {
    id: 'youtube-planner',
    title: 'YouTube Planner',
    description: 'A Notion planner for organizing YouTube content.',
    category: 'Templates',
    platform: 'Notion',
    image: 'https://imgproxy.fourthwall.dev/4d0ffrsHKZtSvppFlQ2h_Ws8viA0DH89YPkAEvGKXW8/w:1920/sm:1/enc/vV8-6LsnclKeL34k/3FtrLE2RyEhtzVaJ/LGF7S2B8Fg5Do54n/Ilt7pf8hSGg6jizK/PMgEjC9kkUxlKX8j/A0hFlMciRc_BMO6_/Dkw1kMBuzB-QQTSL/8wwDpSSnS5YGz3Ut/BuKMinfcBYnJcQfd/qro6mJefBCyb5Y7q/XOcfRCOjxMyVyL8x/dY9Do4zB9j1K9ics/2NRXdwWodLBQpwbc/HMEEzQ.webp',
    destinationUrl: `${fourthwallBaseUrl}/youtube-planner-notion-template`,
    pricingLabel: 'Paid',
    supportModel: 'paid',
    status: 'active',
    sortOrder: 67,
    source: 'fourthwall',
  },
  {
    id: 'youtube-planner-plus',
    title: 'YouTube Planner Plus',
    description: 'A deeper Notion system for YouTube planning.',
    category: 'Templates',
    platform: 'Notion',
    image: '/images/warehouse/products/youtube-planner-plus.png',
    destinationUrl: `${fourthwallBaseUrl}/youtube-planner-plus-notion-template`,
    pricingLabel: 'Paid',
    supportModel: 'paid',
    status: 'active',
    sortOrder: 68,
    source: 'fourthwall',
  },
  {
    id: 'youtool-seo-handbook',
    title: 'YouTool.io SEO Handbook',
    description: 'A free YouTube SEO reference for creators.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/youtool-seo-handbook.png',
    destinationUrl: `${fourthwallBaseUrl}/youtool-io-seo-handbook-for-youtube`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 69,
    source: 'fourthwall',
  },
  {
    id: 'dmca-safe-streaming-music',
    title: 'DMCA Safe Streaming Music',
    description: 'An 80-hour playlist of copyright-safe streaming music.',
    category: 'Creator Utilities',
    platform: 'Spotify',
    image: '/images/warehouse/products/spotify-playlist.png',
    destinationUrl: 'https://open.spotify.com/playlist/0YCHie7xPtBwavLn8SbBNb',
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 70,
    source: 'extra-links',
  },
  {
    id: 'discord-template',
    title: 'Free Discord Template',
    description: 'A pre-configured Discord server template for content creators.',
    category: 'Templates',
    platform: 'Discord',
    image: '/images/warehouse/products/discord-template.png',
    destinationUrl: 'https://discord.com/template/CnvsmH38kruM',
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 80,
    source: 'extra-links',
  },
  {
    id: 'fortnite-healthbar-template',
    title: 'Fortnite Custom Healthbar',
    description: 'After Effects stream overlay for Fortnite creators.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/fortnite-healthbar.webp',
    destinationUrl: `${fourthwallBaseUrl}/fortnite-healthbar-stream-overlay-after-effects`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 90,
    source: 'extra-links',
  },
  {
    id: 'apex-banner-template',
    title: 'Apex Legends Healthbar Overlay',
    description: 'After Effects healthbar overlay for Apex streams.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/gumroad-apex-healthbar.jpg',
    destinationUrl: `${fourthwallBaseUrl}/apex-legends-healthbar-stream-overlay-after-effects`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 100,
    source: 'extra-links',
  },
  {
    id: 'warzone-banner-template',
    title: 'Warzone 2 Healthbar Overlay',
    description: 'After Effects healthbar overlay for Warzone streams.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/gumroad-warzone-healthbar.jpg',
    destinationUrl: `${fourthwallBaseUrl}/warzone-2-healthbar-stream-overlay-after-effects`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 110,
    source: 'extra-links',
  },
  {
    id: 'free-transition-download',
    title: 'Stinger Transition',
    description: 'After Effects stinger transition for streaming.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/gumroad-stinger-transition.jpg',
    destinationUrl: `${fourthwallBaseUrl}/stinger-transition-for-streaming-after-effects`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 120,
    source: 'extra-links',
  },
  {
    id: 'arc-raiders-healthbar-overlay',
    title: 'Arc Raiders Healthbar Overlay',
    description: 'Custom stream overlay for Arc Raiders.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/arc-raiders-healthbar.jpg',
    destinationUrl: `${fourthwallBaseUrl}/arc-raiders-custom-healthbar-overlay`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 130,
    source: 'fourthwall',
  },
  {
    id: 'warzone-stream-overlay-davinci',
    title: 'Warzone 2 Stream Overlay',
    description: 'DaVinci Resolve stream overlay for Warzone.',
    category: 'Creator Utilities',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/gumroad-warzone-davinci.jpg',
    destinationUrl: `${fourthwallBaseUrl}/warzone-2-stream-overlay-davinci-resolve`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 140,
    source: 'fourthwall',
  },
  {
    id: 'text-animation-presets',
    title: 'Text Animation Presets #1',
    description: 'Premiere Pro text animation preset pack.',
    category: 'Premiere Pro',
    platform: 'Gumroad',
    image: '/images/warehouse/products/gumroad-text-animation-presets.jpg',
    destinationUrl: `${fourthwallBaseUrl}/premiere-pro-text-animation-presets-pack-1`,
    pricingLabel: 'Paid',
    supportModel: 'paid',
    status: 'active',
    sortOrder: 155,
    source: 'fourthwall',
  },
  {
    id: 'ultimate-stream-graphics-bundle',
    title: 'Ultimate Stream Graphics Bundle',
    description: 'A Gumroad bundle of stream graphics.',
    category: 'Creator Utilities',
    platform: 'Gumroad',
    image: '/images/warehouse/products/gumroad-stream-graphics-bundle.png',
    destinationUrl: 'https://austindavenport.gumroad.com/l/Streambundle',
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 156,
    source: 'gumroad',
  },
  {
    id: 'giftbox',
    title: 'GIFTBOX',
    description: 'AI-generated instrumental background music.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/giftbox-drop-001.avif',
    destinationUrl: `${fourthwallBaseUrl}/giftbox`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 150,
    source: 'fourthwall',
  },
  {
    id: 'giftbox-2',
    title: 'GIFTBOX 2',
    description: 'A second free drop of instrumental background music.',
    category: 'Digital Products',
    platform: 'Fourthwall',
    image: '/images/warehouse/products/fourthwall-giftbox-2.jpg',
    destinationUrl: `${fourthwallBaseUrl}/giftbox-2`,
    pricingLabel: 'Free',
    supportModel: 'free',
    status: 'active',
    sortOrder: 151,
    source: 'fourthwall',
  },
];

export const visibleToolCatalog = toolCatalog
  .filter(tool => tool.status === 'active' || tool.status === 'coming-soon')
  .sort((a, b) => a.sortOrder - b.sortOrder);

export const getToolById = (id: string): ToolItem | undefined =>
  toolCatalog.find(tool => tool.id === id);
